import React, {Suspense, useContext} from 'react';
import {CSSObject, styled, Theme} from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import SidebarMenu from './components/sidebarMenu';
import {SidebarContext} from '../../contexts/SidebarContext';
import {ReactComponent as RexplorerLogo} from '../../assets/logo_rexplorer.svg';
import {ReactComponent as RexplorerLogoSmall} from '../../assets/logo.svg';
import {Link} from '@mui/material';
import Profile from './components/profile';
import Icon from '../../components/Icon/Icon';
import routes from '../../routes';

const drawerWidth = 225;

const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    background: '#F8F9F8',
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    background: '#F8F9F8',
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(9)} + 1px)`,
    },
});

const TopSection = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '64px',
    marginBottom: '25px'
});

const Drawer = styled(MuiDrawer, {
    shouldForwardProp: prop => prop !== 'open',
})(({ theme, open }) => ({
    width: drawerWidth,
    color: '#09140E',
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
    }),
}));

export default function Sidebar() {
    const { sidebarToggle, toggleSidebar } = useContext(SidebarContext);

    const renderDrawerIcon = () => {
        if (sidebarToggle) {
            return (
                <IconButton onClick={toggleSidebar}>
                    <ChevronLeftIcon />
                </IconButton>
            );
        } else {
            return (
                <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    onClick={toggleSidebar}
                    edge="start"
                    sx={{
                        display: sidebarToggle ? 'none' : 'flex',
                    }}
                >
                    <Icon icon="menu" />
                </IconButton>
            );
        }
    };

    return (
        <>
            <Drawer
                variant="permanent"
                open={sidebarToggle}
                sx={{ backgroundColor: '#F8F9F8' }}
            >
                <TopSection>
                    <Link href={routes.APP.ROOT}>
                        {sidebarToggle ? (
                            <RexplorerLogo />
                        ) : (
                            <RexplorerLogoSmall />
                        )}
                    </Link>
                </TopSection>
                <SidebarMenu />
                <Profile />
                <Divider sx={{ borderColor: '#E3E7E4' }} />
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: sidebarToggle ? 'flex-end' : 'center',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        {renderDrawerIcon()}
                    </Box>
                </Box>
            </Drawer>
        </>
    );
}
